import { useEffect } from 'react';
import { BRAND_ASSETS } from './brand';
import type { StructuredDataSchema } from './seo';

interface PageMetaInput {
  title: string;
  description: string;
  canonicalPath?: string;
  robots?: string;
  type?: 'website' | 'article';
  imagePath?: string;
  structuredData?: StructuredDataSchema[];
}

function upsertMeta(selector: string, create: () => HTMLMetaElement): HTMLMetaElement {
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  if (existing) {
    return existing;
  }

  const next = create();
  document.head.appendChild(next);
  return next;
}

function upsertLink(selector: string, create: () => HTMLLinkElement): HTMLLinkElement {
  const existing = document.head.querySelector<HTMLLinkElement>(selector);
  if (existing) {
    return existing;
  }

  const next = create();
  document.head.appendChild(next);
  return next;
}

function upsertScript(selector: string, create: () => HTMLScriptElement): HTMLScriptElement {
  const existing = document.head.querySelector<HTMLScriptElement>(selector);
  if (existing) {
    return existing;
  }

  const next = create();
  document.head.appendChild(next);
  return next;
}

export function setRobotsMeta(content: string): void {
  upsertMeta('meta[name="robots"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'robots');
    return meta;
  }).setAttribute('content', content);

  upsertMeta('meta[name="googlebot"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'googlebot');
    return meta;
  }).setAttribute('content', content);
}

export function setCanonicalLink(href: string): void {
  upsertLink('link[rel="canonical"]', () => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    return link;
  }).setAttribute('href', href);
}

export function setOpenGraphUrl(content: string): void {
  upsertMeta('meta[property="og:url"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:url');
    return meta;
  }).setAttribute('content', content);
}

export function setStructuredDataScripts(items: StructuredDataSchema[]): void {
  document.head.querySelectorAll('script[data-acc-structured-data="true"]').forEach((node) => {
    node.remove();
  });

  items.forEach((item, index) => {
    upsertScript(`script[data-acc-structured-data="true"][data-acc-structured-data-index="${index}"]`, () => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-acc-structured-data', 'true');
      script.setAttribute('data-acc-structured-data-index', String(index));
      return script;
    }).textContent = JSON.stringify(item);
  });
}

export function usePageMeta(input: PageMetaInput): void {
  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    const canonicalUrl = new URL(input.canonicalPath ?? window.location.pathname, window.location.origin).toString();
    const imageUrl = new URL(input.imagePath ?? BRAND_ASSETS.appIconPng, window.location.origin).toString();

    document.title = input.title;

    upsertMeta('meta[name="description"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      return meta;
    }).setAttribute('content', input.description);

    setRobotsMeta(input.robots ?? 'index,follow');

    upsertMeta('meta[property="og:title"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      return meta;
    }).setAttribute('content', input.title);

    upsertMeta('meta[property="og:description"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:description');
      return meta;
    }).setAttribute('content', input.description);

    upsertMeta('meta[property="og:type"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:type');
      return meta;
    }).setAttribute('content', input.type ?? 'website');

    setOpenGraphUrl(canonicalUrl);

    upsertMeta('meta[property="og:image"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:image');
      return meta;
    }).setAttribute('content', imageUrl);

    upsertMeta('meta[property="og:site_name"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:site_name');
      return meta;
    }).setAttribute('content', 'AbleComply');

    upsertMeta('meta[name="twitter:card"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'twitter:card');
      return meta;
    }).setAttribute('content', 'summary_large_image');

    upsertMeta('meta[name="twitter:title"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'twitter:title');
      return meta;
    }).setAttribute('content', input.title);

    upsertMeta('meta[name="twitter:description"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'twitter:description');
      return meta;
    }).setAttribute('content', input.description);

    upsertMeta('meta[name="twitter:image"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'twitter:image');
      return meta;
    }).setAttribute('content', imageUrl);

    setCanonicalLink(canonicalUrl);
    setStructuredDataScripts(input.structuredData ?? []);
  }, [
    input.canonicalPath,
    input.description,
    input.imagePath,
    input.robots,
    input.structuredData,
    input.title,
    input.type,
  ]);
}
